import flatMap from "lodash/flatMap"

import MatrixStore from "@components/ui/Spreadsheet/state/MatrixStore"
import UnifiedMatrixController from "@store/unified-matrix/unified-matrix.controller"
import {
  arrayOfStrings,
  defaultOptionalStringValidator,
  defaultStringValidator,
  stringOrArrayOfStrings,
} from "@components/ui/Spreadsheet/validation"
import mainRoutes from "@root/main.routes"
import { WorkbookReportTypeName } from "@framework/types/workbook-report"

export const initCustomFunctions = (
  matrixStore: MatrixStore,
  unifiedMatrixController: UnifiedMatrixController
) => {
  initThemeFunction("THEME", matrixStore, unifiedMatrixController)

  initAccountPlanFunction("ACCOUNTPLAN", matrixStore, unifiedMatrixController)

  initProductRecommendationFunction(
    "PRODUCTRECOMMENDATION",
    matrixStore,
    unifiedMatrixController
  )

  initProductComparisonFunction(
    "PRODUCTCOMPARISON",
    matrixStore,
    unifiedMatrixController
  )

  initProductPositioningFunction(
    "PRODUCTPOSITIONING",
    matrixStore,
    unifiedMatrixController
  )

  initDocumentComplianceFunction(
    "DOCUMENTCOMPLIANCE",
    matrixStore,
    unifiedMatrixController
  )

  initUpCrossSellOpportunitiesFunction(
    "UPCROSSSELLOPPORTUNITY",
    matrixStore,
    unifiedMatrixController
  )
}

export const initThemeFunction = (
  functionName: string,
  matrixStore: MatrixStore,
  unifiedMatrixController: UnifiedMatrixController
) => {
  matrixStore.editManager.functionManager.registerFunction<
    [string, string, string | undefined]
  >({
    name: functionName,
    description: "Generate themes for a Company by Category and Business Unit",
    requiredArgumentsNum: 2,
    maxArgumentsNum: 3,
    arguments: [
      {
        displayName: "Company",
        validator: defaultStringValidator,
      },
      {
        displayName: "Category",
        validator: defaultStringValidator,
      },
      {
        displayName: "Business Unit",
        validator: defaultStringValidator,
      },
    ],
    transformArgs: (args: any[]) => {
      return args as [string, string, string | undefined]
    },
    handler: async (...args) => {
      const response = await unifiedMatrixController.generateThemes(...args)

      if (response.status === "FAILED")
        throw new Error("Failed to generate themes.")

      return response.data
    },
  })
}

export const initAccountPlanFunction = (
  functionName: WorkbookReportTypeName,
  matrixStore: MatrixStore,
  unifiedMatrixController: UnifiedMatrixController
) => {
  matrixStore.editManager.functionManager.registerFunction<
    [string, string, string?, string?]
  >({
    name: functionName,
    description: "Generate account plan for a company.",
    requiredArgumentsNum: 2,
    maxArgumentsNum: 4,
    arguments: [
      {
        displayName: "Target Company's Url",
        validator: defaultStringValidator,
      },
      { displayName: "My Company's Url", validator: defaultStringValidator },
      {
        displayName: "Target Company's Business Unit (optional)",
        validator: defaultOptionalStringValidator,
      },
      {
        displayName: "Additional Context (optional)",
        validator: defaultOptionalStringValidator,
      },
    ],
    transformArgs: (args: any[]) => {
      return args as [string, string, string?, string?]
    },
    handler: async (...args) => {
      const response = await unifiedMatrixController.generateAccountPlan(
        ...args
      )

      if (response.status === "FAILED")
        throw new Error("Failed to generate account plan.")

      return encodeURI(
        `${window.location.origin}${mainRoutes.workbookReport(
          response.data.id
        )}`
      )
    },
  })
}

export const initProductComparisonFunction = (
  functionName: WorkbookReportTypeName,
  matrixStore: MatrixStore,
  unifiedMatrixController: UnifiedMatrixController
) => {
  matrixStore.editManager.functionManager.registerFunction<[string[]]>({
    name: functionName,
    description: "Generate product comparison report.",
    requiredArgumentsNum: 1,
    maxArgumentsNum: 1,
    arguments: [
      {
        displayName: "Product Names",
        validator: arrayOfStrings,
      },
    ],
    transformArgs: (args: any[]) => {
      const productNames = flatMap(args[0])

      return [productNames.filter((productName) => !!productName)] as [string[]]
    },
    handler: async (...args) => {
      const response =
        await unifiedMatrixController.generateProductComparisonReport(...args)

      if (response.status === "FAILED")
        throw new Error("Failed to generate product comparison report.")

      return encodeURI(
        `${window.location.origin}${mainRoutes.workbookReport(
          response.data.id
        )}`
      )
    },
  })
}

export const initProductPositioningFunction = (
  functionName: WorkbookReportTypeName,
  matrixStore: MatrixStore,
  unifiedMatrixController: UnifiedMatrixController
) => {
  matrixStore.editManager.functionManager.registerFunction<
    [string, string, string[]]
  >({
    name: functionName,
    description:
      "Generate product positioning report for a target product and one or more alternate products.",
    requiredArgumentsNum: 3,
    maxArgumentsNum: 10,
    arguments: [
      {
        displayName: "Application",
        validator: defaultStringValidator,
      },
      {
        displayName: "Target Product",
        validator: defaultStringValidator,
      },
      {
        displayName: "Alternate Product",
        validator: stringOrArrayOfStrings,
      },
    ],
    transformArgs: (args: any[]) => {
      const application = args[0]

      const target = args[1]

      const altProducts = flatMap(args.slice(2))

      return [application, target, altProducts]
    },
    handler: async (...args) => {
      const response =
        await unifiedMatrixController.generateProductPositioningReport(...args)

      if (response.status === "FAILED")
        throw new Error("Failed to generate product positioning report.")

      return encodeURI(
        `${window.location.origin}${mainRoutes.workbookReport(
          response.data.id
        )}`
      )
    },
  })
}

export const initDocumentComplianceFunction = (
  functionName: WorkbookReportTypeName,
  matrixStore: MatrixStore,
  unifiedMatrixController: UnifiedMatrixController
) => {
  matrixStore.editManager.functionManager.registerFunction<[string, string]>({
    name: functionName,
    description:
      "Generate compliance report for an authority document and regulated document.",
    requiredArgumentsNum: 2,
    maxArgumentsNum: 2,
    arguments: [
      {
        displayName: "Authority Document",
        validator: defaultStringValidator,
      },
      {
        displayName: "Regulated Document",
        validator: defaultStringValidator,
      },
    ],
    transformArgs: (args: any[]) => {
      return args as [string, string]
    },
    handler: async (...args) => {
      const response =
        await unifiedMatrixController.generateDocumentComplianceReport(...args)

      if (response.status === "FAILED")
        throw new Error("Failed to generate document compliance report.")

      return encodeURI(
        `${window.location.origin}${mainRoutes.workbookReport(
          response.data.id
        )}`
      )
    },
  })
}

export const initProductRecommendationFunction = (
  functionName: WorkbookReportTypeName,
  matrixStore: MatrixStore,
  unifiedMatrixController: UnifiedMatrixController
) => {
  matrixStore.editManager.functionManager.registerFunction<[string]>({
    name: functionName,
    description:
      "Generate product recommendation report for a target application, scenario or metrics.",
    requiredArgumentsNum: 1,
    maxArgumentsNum: 1,
    arguments: [
      {
        displayName: "Context",
        validator: defaultStringValidator,
      },
    ],
    transformArgs: (args: any[]) => {
      const context = args[0] as string

      return [context]
    },
    handler: async (...args) => {
      const response =
        await unifiedMatrixController.generateProductRecommendationReport(
          ...args
        )

      if (response.status === "FAILED")
        throw new Error("Failed to generate product recommendation report.")

      return encodeURI(
        `${window.location.origin}${mainRoutes.workbookReport(
          response.data.id
        )}`
      )
    },
  })
}

export const initUpCrossSellOpportunitiesFunction = (
  functionName: string,
  matrixStore: MatrixStore,
  unifiedMatrixController: UnifiedMatrixController
) => {
  matrixStore.editManager.functionManager.registerFunction<[string]>({
    name: functionName,
    description:
      "Generate Up and Cross sell opportunities report for and account.",
    requiredArgumentsNum: 1,
    maxArgumentsNum: 1,
    arguments: [
      {
        displayName: "Account Name",
        validator: defaultStringValidator,
      },
    ],
    transformArgs: (args: any[]) => {
      const accountName = args[0] as string

      return [accountName]
    },
    handler: async (...args) => {
      const response =
        await unifiedMatrixController.generateUpCrossSellOpportunitiesReport(
          ...args
        )

      if (response.status === "FAILED")
        throw new Error("Failed to generate Up/Cross Sell Opportunity report")

      return encodeURI(
        `${window.location.origin}${mainRoutes.workbookReport(
          response.data.id
        )}`
      )
    },
  })
}
